import { html, nothing, type TemplateResult } from 'lit';

export interface CompanyPortalDocumentViewModel {
  id: string;
  label: string;
  url?: string;
}

export interface CompanyPortalCandidateViewModel {
  id: string;
  firstName: string;
  origin?: string;
  qualification?: string;
  age?: number;
  focusAreas?: string[];
  workExperienceYears?: string;
  experience?: string;
  skills?: string;
  education?: string;
  currentProfession?: string;
  germanLevel?: string;
  profileImageUrl?: string;
  documents?: CompanyPortalDocumentViewModel[];
}

export interface CompanyPortalLabels {
  firstName: string;
  age: string;
  origin: string;
  experienceYears: string;
  germanLevel: string;
  focusAreas: string;
  profileDetails: string;
  currentProfession: string;
  education: string;
  skills: string;
  experience: string;
  documents: string;
  status: string;
  requestInterest: string;
  interestRequested: string;
  withdrawQuestion: string;
  withdrawDescription: string;
  keepInterest: string;
  withdraw: string;
  documentUnavailable: string;
  photoAlt: (name: string) => string;
  ageValue: (age: number) => string;
}

export interface CompanyPortalCardState {
  interested: boolean;
  busy?: boolean;
  expanded?: boolean;
  showWithdrawConfirmation?: boolean;
  statusLabel?: string;
  statusTone?: 'is-new' | 'is-progress' | 'is-complete' | 'is-muted';
  feedback?: string;
  feedbackTone?: 'is-success' | 'is-error' | 'is-info';
}

export interface CompanyPortalActions {
  onToggleInterest: () => void;
  onConfirmWithdraw?: () => void;
  onKeepInterest?: () => void;
  onDocument?: (document: CompanyPortalDocumentViewModel) => void;
  onImageError?: () => void;
}

export type CompanyPortalFieldRenderer = (path: string, value: string) => unknown;

export interface CompanyPortalIdentityProps {
  renderField?: CompanyPortalFieldRenderer;
  renderImage?: (path: string, url: string, alt: string) => unknown;
  companyName: string;
  eyebrow: string;
  initials: string;
  logoUrl?: string;
  logoAlt?: string;
  logoLoading?: boolean;
  logoFailed?: boolean;
  onLogoError?: () => void;
  cobrandLabel: string;
  cobrandLogoUrl: string;
  cobrandLogoAlt?: string;
}

export const companyPortalIdentityTemplate = ({
  renderField = (_path, value) => value,
  renderImage,
  companyName,
  eyebrow,
  initials,
  logoUrl,
  logoAlt = companyName,
  logoLoading = false,
  logoFailed = false,
  onLogoError,
  cobrandLabel,
  cobrandLogoUrl,
  cobrandLogoAlt = 'Viva Porta',
}: CompanyPortalIdentityProps): TemplateResult => html`
  <div class="vp-company-brand-group">
    <div class="vp-company-identity" aria-label=${companyName}>
      <div class=${`vp-company-logo-frame ${logoLoading ? 'is-loading' : ''}`}>
        ${logoLoading
          ? html`<span class="vp-company-logo-skeleton" aria-hidden="true"></span>`
          : logoUrl && !logoFailed
            ? renderImage ? renderImage('logoUrl', logoUrl, logoAlt) : html`<img src=${logoUrl} alt=${logoAlt} @error=${onLogoError} />`
            : html`<span class="vp-company-logo-initials" aria-hidden="true">${renderField('initials', initials)}</span>`}
      </div>
      <div class="vp-company-identity-copy">
        ${logoLoading
          ? html`<span class="vp-company-text-skeleton is-short" aria-hidden="true"></span><span class="vp-company-text-skeleton" aria-hidden="true"></span>`
          : html`<span>${renderField('eyebrow', eyebrow)}</span><strong>${renderField('companyName', companyName)}</strong>`}
      </div>
    </div>
    <div class="vp-company-cobrand" aria-label=${cobrandLogoAlt}>
      <span>${renderField('cobrandLabel', cobrandLabel)}</span>
      ${renderImage ? renderImage('cobrandLogoUrl', cobrandLogoUrl, cobrandLogoAlt) : html`<img src=${cobrandLogoUrl} alt=${cobrandLogoAlt} />`}
    </div>
  </div>
`;

export type FilterableCompanyProfile = Pick<
  CompanyPortalCandidateViewModel,
  | 'firstName'
  | 'origin'
  | 'qualification'
  | 'age'
  | 'focusAreas'
  | 'workExperienceYears'
  | 'experience'
  | 'skills'
  | 'education'
  | 'currentProfession'
  | 'germanLevel'
>;

export const normalizeCompanyProfileSearchValue = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/ß/gu, 'ss')
    .replace(/ı/gu, 'i')
    .toLocaleLowerCase()
    .trim();

export const availableCompanyFocusAreas = (profiles: FilterableCompanyProfile[]): string[] => {
  const areas = new Map<string, string>();
  for (const profile of profiles) {
    for (const area of profile.focusAreas ?? []) {
      const trimmedArea = area.trim();
      const normalizedArea = normalizeCompanyProfileSearchValue(trimmedArea);
      if (normalizedArea && !areas.has(normalizedArea)) areas.set(normalizedArea, trimmedArea);
    }
  }
  return [...areas.values()];
};

export const filterCompanyProfiles = <T extends FilterableCompanyProfile>(
  profiles: T[],
  searchQuery: string,
  focusArea: string,
): T[] => {
  const normalizedQuery = normalizeCompanyProfileSearchValue(searchQuery);
  const normalizedFocusArea = normalizeCompanyProfileSearchValue(focusArea);
  return profiles.filter(profile => {
    const focusAreas = profile.focusAreas ?? [];
    if (normalizedFocusArea && !focusAreas.some(area => normalizeCompanyProfileSearchValue(area) === normalizedFocusArea)) return false;
    if (!normalizedQuery) return true;
    return [
      profile.firstName,
      profile.origin,
      profile.qualification,
      profile.age?.toString(),
      profile.workExperienceYears,
      profile.experience,
      profile.skills,
      profile.education,
      profile.currentProfession,
      profile.germanLevel,
      ...focusAreas,
    ].some(value => value && normalizeCompanyProfileSearchValue(value).includes(normalizedQuery));
  });
};

export interface CompanyPortalCandidateCardProps {
  renderField?: CompanyPortalFieldRenderer;
  renderImage?: (url: string, alt: string) => unknown;
  candidate: CompanyPortalCandidateViewModel;
  labels: CompanyPortalLabels;
  state: CompanyPortalCardState;
  actions: CompanyPortalActions;
  imageFailed?: boolean;
  focusAreaLabel?: (area: string) => string;
  experienceYearsLabel?: (years: string) => string;
  germanLevelLabel?: (level: string) => string;
}

const detailValue = (value: string | undefined) => value?.trim() || '';

export const companyPortalCandidateCardTemplate = ({
  renderField = (_path, value) => value,
  renderImage,
  candidate,
  labels,
  state,
  actions,
  imageFailed = false,
  focusAreaLabel = value => value,
  experienceYearsLabel = value => value,
  germanLevelLabel = value => value,
}: CompanyPortalCandidateCardProps): TemplateResult => {
  const name = candidate.firstName.trim() || '—';
  const details = [
    ['currentProfession', labels.currentProfession, detailValue(candidate.currentProfession)],
    ['education', labels.education, detailValue(candidate.education)],
    ['skills', labels.skills, detailValue(candidate.skills)],
    ['experience', labels.experience, detailValue(candidate.experience)],
  ].filter((entry): entry is [string, string, string] => Boolean(entry[2]));
  const documents = candidate.documents ?? [];
  return html`
    <article class=${`vp-company-candidate ${state.interested ? 'has-interest' : ''}`}>
      <div class="vp-company-candidate-head">
        <div class="vp-company-candidate-photo">
          ${candidate.profileImageUrl && !imageFailed
            ? renderImage ? renderImage(candidate.profileImageUrl, labels.photoAlt(name)) : html`<img src=${candidate.profileImageUrl} alt=${labels.photoAlt(name)} loading="lazy" decoding="async" @error=${actions.onImageError} />`
            : html`<span class="vp-company-candidate-initial" aria-hidden="true">${name.slice(0, 1).toLocaleUpperCase()}</span>`}
        </div>
        <div class="vp-company-candidate-name"><span>${renderField('labels.firstName', labels.firstName)}</span><h2>${renderField('firstName', name)}</h2><p>${renderField('qualification', candidate.qualification || '—')}</p></div>
        ${state.statusLabel ? html`<span class=${`vp-company-status ${state.statusTone ?? 'is-progress'}`}><span aria-hidden="true"></span>${state.statusLabel}</span>` : nothing}
      </div>
      <dl class="vp-company-candidate-highlights">
        ${candidate.age ? html`<div><dt><span class="icon-[material-symbols--cake-outline-rounded]" aria-hidden="true"></span>${renderField('labels.age', labels.age)}</dt><dd>${renderField('age', labels.ageValue(candidate.age))}</dd></div>` : nothing}
        <div><dt><span class="icon-[material-symbols--location-on-outline-rounded]" aria-hidden="true"></span>${renderField('labels.origin', labels.origin)}</dt><dd>${renderField('origin', candidate.origin || '—')}</dd></div>
        ${candidate.workExperienceYears ? html`<div><dt><span class="icon-[material-symbols--work-history-outline-rounded]" aria-hidden="true"></span>${renderField('labels.experienceYears', labels.experienceYears)}</dt><dd>${renderField('workExperienceYears', experienceYearsLabel(candidate.workExperienceYears))}</dd></div>` : nothing}
        ${candidate.germanLevel ? html`<div><dt><span class="icon-[material-symbols--translate-rounded]" aria-hidden="true"></span>${renderField('labels.germanLevel', labels.germanLevel)}</dt><dd>${renderField('germanLevel', germanLevelLabel(candidate.germanLevel))}</dd></div>` : nothing}
      </dl>
      ${(candidate.focusAreas?.length ?? 0) > 0 ? html`<section class="vp-company-focus-areas" aria-label=${labels.focusAreas}><h3><span class="icon-[material-symbols--category-outline-rounded]" aria-hidden="true"></span>${renderField('labels.focusAreas', labels.focusAreas)}</h3><ul>${candidate.focusAreas!.map((area, index) => html`<li>${renderField(`focusAreas.${index}`, focusAreaLabel(area))}</li>`)}</ul></section>` : nothing}
      ${details.length ? html`<details class="vp-company-profile-details"><summary><span class="icon-[material-symbols--badge-outline-rounded]" aria-hidden="true"></span><span>${renderField('labels.profileDetails', labels.profileDetails)}</span><span class="icon-[material-symbols--keyboard-arrow-down-rounded]" aria-hidden="true"></span></summary><dl>${details.map(([key, label, value]) => html`<div><dt>${renderField(`labels.${key}`, label)}</dt><dd>${renderField(key, value)}</dd></div>`)}</dl></details>` : nothing}
      ${documents.length ? html`<section class="vp-company-documents" aria-labelledby=${`documents-${candidate.id}`}><h3 id=${`documents-${candidate.id}`}><span class="icon-[material-symbols--folder-open-outline-rounded]" aria-hidden="true"></span>${renderField('labels.documents', labels.documents)}</h3><div>${documents.map((document, index) => html`<button type="button" @click=${() => actions.onDocument?.(document)}><span class="icon-[material-symbols--description-outline-rounded]" aria-hidden="true"></span><span>${renderField(`documents.${index}.label`, document.label)}</span><span class="icon-[material-symbols--lock-outline]" aria-hidden="true"></span></button>`)}</div></section>` : nothing}
      <div class="vp-company-interest-area">
        <span class="vp-company-interest-label">${renderField('labels.status', labels.status)}</span>
        <button type="button" class=${`vp-company-interest-button ${state.interested ? 'is-active' : ''}`} ?disabled=${state.busy} aria-pressed=${state.interested ? 'true' : 'false'} @click=${actions.onToggleInterest}>
          <span class=${`${state.interested ? 'icon-[material-symbols--favorite-rounded]' : 'icon-[material-symbols--favorite-outline-rounded]'} vp-company-interest-heart`} aria-hidden="true"></span>
          <span>${renderField(state.interested ? 'labels.interestRequested' : 'labels.requestInterest', state.interested ? labels.interestRequested : labels.requestInterest)}</span>
        </button>
        ${state.showWithdrawConfirmation ? html`<div class="vp-company-withdraw-confirmation" role="group" aria-label=${labels.withdrawQuestion}><div><strong>${renderField('labels.withdrawQuestion', labels.withdrawQuestion)}</strong><p>${renderField('labels.withdrawDescription', labels.withdrawDescription)}</p></div><div class="vp-company-withdraw-actions"><button type="button" class="vp-company-secondary-button" @click=${actions.onKeepInterest}>${renderField('labels.keepInterest', labels.keepInterest)}</button><button type="button" class="vp-company-danger-button" @click=${actions.onConfirmWithdraw}>${renderField('labels.withdraw', labels.withdraw)}</button></div></div>` : nothing}
        ${state.feedback ? html`<p class=${`vp-company-card-feedback ${state.feedbackTone ?? 'is-info'}`} role=${state.feedbackTone === 'is-error' ? 'alert' : 'status'}>${state.feedback}</p>` : nothing}
      </div>
    </article>
  `;
};
